/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState, useCallback, useRef } from 'react';
import { LatLngBounds, LatLngExpression, Map as LeafletMap } from 'leaflet';
import { MdMyLocation, MdDelete } from 'react-icons/md';
import { AiOutlinePlus, AiOutlineMinus } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import {
    ROUTES_AUTH,
    ROUTES_OWNER_PANEL,
    trackingEvents,
    QUERIES,
} from '@constants';

import { Button, Map } from 'components';
import { InputText, Select, DatePicker } from 'components/Forms';
import { useAuth, useLayout, useTracking } from 'contexts';
import {
    calculatePolygonArea,
    onSuccessMessage,
    onErrorMessageStr,
    onConfirmDangerMessage,
} from 'helpers';
import {
    useCreateOwnerField,
    useGetOwnerFieldDetail,
    useUpdateOwnerField,
} from 'services/owner/field';
import { convertFloatToBR } from 'utils';

import {
    Container,
    ActionButtons,
    ActionButtonsContainer,
    ZoomButtonsContainer,
    FormContainer,
} from './Form.style';
import {
    MapController,
    FieldPolygon,
    AreaInfo,
    FieldMarker,
    PseudoMarker,
    HistoryController,
} from './components';

interface FormProps {
    id?: string;
}

export const PanelOwnerFieldForm = ({ id }: FormProps) => {
    const { t } = useTranslation();
    const { account, idAccess } = useAuth();
    const { initLayout } = useLayout();
    const { trackEvent } = useTracking();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [fieldName, setFieldName] = useState('');
    const [description, setDescription] = useState('');
    const [rent, setRent] = useState(false);
    const [datePurchase, setDatePurchase] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [coordinates, setCoordinates] = useState<
        Array<{ latitude: number; longitude: number }>
    >([]);
    const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<
        number | null
    >(null);
    const [area, setArea] = useState(0);
    const [isUsingCurrentLocation, setIsUsingCurrentLocation] = useState(true);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [history, setHistory] = useState<
        Array<Array<{ latitude: number; longitude: number }>>
    >([]);
    const [isDraggingMarker, setIsDraggingMarker] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(4);

    const mapRef = useRef<LeafletMap | null>(null);

    const { data: fieldData, isLoading: isLoadingFieldData } =
        useGetOwnerFieldDetail(id);

    const { mutateAsync: createField, isLoading: isLoadingCreateField } =
        useCreateOwnerField();
    const { mutateAsync: updateField, isLoading: isLoadingUpdateField } =
        useUpdateOwnerField(id || '');

    useEffect(() => {
        initLayout(t(id ? 'fieldFormEditTitle' : 'fieldFormTitle'), [
            {
                label: t('routesAccountSelect'),
                link: ROUTES_AUTH.ACCOUNT_SELECTED.fullPath,
            },
            {
                label: t('routesField'),
                link: ROUTES_OWNER_PANEL.FIELD.fullPath,
            },
            {
                label: t(id ? 'fieldFormEditArea' : 'fieldFormNewArea'),
                link: '#',
            },
        ]);

        trackEvent(trackingEvents.fieldFormPageView);
    }, [t, id]);

    useEffect(() => {
        if (fieldData && id) {
            setFieldName(fieldData.name || '');
            setDescription(fieldData.description || '');
            setRent(fieldData.rent || false);
            setDatePurchase(fieldData.datePurchase || '');
            setIsActive(
                fieldData.isActive !== undefined ? fieldData.isActive : true
            );

            if (fieldData.coordinates) {
                try {
                    const parsedCoordinates = JSON.parse(fieldData.coordinates);
                    setCoordinates(parsedCoordinates);
                    setIsUsingCurrentLocation(false);
                } catch (error) {
                    console.error('Error parsing coordinates:', error);
                }
            }
        }
    }, [fieldData, id]);

    const addToHistory = useCallback(() => {
        if (coordinates.length > 0) {
            setHistory((prev) => {
                const newHistory = [...prev, [...coordinates]];
                if (newHistory.length > 20) {
                    newHistory.shift();
                }
                return newHistory;
            });
        }
    }, [coordinates]);

    useEffect(() => {
        if (coordinates.length >= 3) {
            const areaValue = calculatePolygonArea(coordinates);
            setArea(areaValue * (account?.unitySize === 'METER' ? 1 : 0.0001));
        } else {
            setArea(0);
        }
    }, [coordinates, calculatePolygonArea]);

    const handleMapClick = useCallback(
        (e) => {
            if (isDraggingMarker) return;

            addToHistory();
            trackEvent(trackingEvents.fieldFormAddPointClick);

            const { lat, lng } = e.latlng;
            setCoordinates((prev) => [
                ...prev,
                {
                    latitude: lat,
                    longitude: lng,
                },
            ]);

            if (isUsingCurrentLocation) {
                setIsUsingCurrentLocation(false);
            }
        },
        [isUsingCurrentLocation, addToHistory, isDraggingMarker, trackEvent]
    );

    const handleMarkerClick = useCallback((index: number) => {
        setSelectedMarkerIndex(index);
    }, []);

    const handleMarkerDragEnd = useCallback(
        (position: [number, number], index: number) => {
            setIsDraggingMarker(false);
            addToHistory();
            trackEvent(trackingEvents.fieldFormDragPointClick);

            setCoordinates((prev) => {
                const newCoordinates = [...prev];
                newCoordinates[index] = {
                    latitude: position[0],
                    longitude: position[1],
                };
                return newCoordinates;
            });
        },
        [addToHistory, trackEvent]
    );

    const handlePseudoMarkerClick = useCallback(
        (position: [number, number], index: number) => {
            addToHistory();
            trackEvent(trackingEvents.fieldFormPseudoMarkerClick);

            setCoordinates((prev) => {
                const newCoordinates = [...prev];
                newCoordinates.splice(index, 0, {
                    latitude: position[0],
                    longitude: position[1],
                });
                return newCoordinates;
            });

            setSelectedMarkerIndex(index);
        },
        [addToHistory, trackEvent]
    );

    const handleDeleteMarker = useCallback(() => {
        if (selectedMarkerIndex === null || coordinates.length <= 3) return;

        addToHistory();
        trackEvent(trackingEvents.fieldFormRemovePointClick);

        setCoordinates((prev) => {
            const newCoordinates = [...prev];
            newCoordinates.splice(selectedMarkerIndex, 1);
            return newCoordinates;
        });

        setSelectedMarkerIndex(null);
    }, [selectedMarkerIndex, coordinates.length, addToHistory, trackEvent]);

    const handleMyLocation = useCallback(() => {
        setIsLoadingLocation(true);
        trackEvent(trackingEvents.fieldFormMyLocationClick);

        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;

                    if (mapRef.current) {
                        mapRef.current.flyTo([latitude, longitude], 15);
                    }

                    setIsLoadingLocation(false);
                },
                (error) => {
                    console.warn('Error getting location:', error);
                    setIsLoadingLocation(false);
                },
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
            );
        } else {
            console.warn('Geolocation not available');
            setIsLoadingLocation(false);
        }
    }, [trackEvent]);

    const saveField = useCallback(async () => {
        const payload = {
            name: fieldName,
            description,
            rent,
            datePurchase,
            isActive,
            coordinates: JSON.stringify(coordinates),
            size: area,
        };

        let response;

        if (id) {
            trackEvent(trackingEvents.fieldFormUpdateAreaClick);
            response = await updateField(payload);
        } else {
            trackEvent(trackingEvents.fieldFormSaveAreaClick);
            response = await createField(payload);
        }

        if (response) {
            trackEvent(
                id
                    ? trackingEvents.fieldFormUpdateAreaSuccess
                    : trackingEvents.fieldFormSaveAreaSuccess
            );
            onSuccessMessage(
                t(
                    id
                        ? 'fieldFormUpdateSuccessMessage'
                        : 'fieldFormSuccessMessage'
                ),
                response.message
            );

            queryClient.refetchQueries([QUERIES.OWNER.FIELD.module, idAccess]);
            if (id) {
                queryClient.refetchQueries([QUERIES.OWNER.FIELD.module, id]);
            } else if (response.id) {
                navigate(
                    `${ROUTES_OWNER_PANEL.FIELD.fullPath}/edit/${response.id}`
                );
            }
        } else {
            trackEvent(
                id
                    ? trackingEvents.fieldFormUpdateAreaError
                    : trackingEvents.fieldFormSaveAreaError
            );
            onErrorMessageStr(
                t(id ? 'fieldFormUpdateErrorMessage' : 'fieldFormErrorMessage'),
                response.message
            );
        }
    }, [
        fieldName,
        description,
        rent,
        datePurchase,
        isActive,
        coordinates,
        area,
        id,
        createField,
        updateField,
        t,
        trackEvent,
        queryClient,
        idAccess,
        navigate,
    ]);

    const resetField = useCallback(async () => {
        trackEvent(trackingEvents.fieldFormClearPointsClick);

        if (
            await onConfirmDangerMessage(
                t('fieldFormResetConfirmTitle'),
                t('fieldFormResetConfirmMessage'),
                t('fieldFormResetConfirmButton'),
                t('fieldFormResetCancelButton')
            )
        ) {
            trackEvent(trackingEvents.fieldFormResetConfirmClick);
            addToHistory();
            setCoordinates([]);
            setSelectedMarkerIndex(null);
            setIsUsingCurrentLocation(true);
        } else {
            trackEvent(trackingEvents.fieldFormResetCancelClick);
        }
    }, [addToHistory, t, trackEvent]);

    const getBounds = useCallback(() => {
        if (coordinates.length < 2) {
            return null;
        }

        const bounds = new LatLngBounds([]);
        coordinates.forEach((point) => {
            bounds.extend([point.latitude, point.longitude]);
        });

        return bounds.isValid() ? bounds : null;
    }, [coordinates]);

    const defaultCenter: LatLngExpression = [-15.788497, -47.879873];
    const bounds = getBounds();

    const getPseudoMarkers = useCallback(() => {
        if (coordinates.length < 2) return null;

        return coordinates.map((point, index) => {
            const nextIndex = (index + 1) % coordinates.length;
            const nextPoint = coordinates[nextIndex];

            const middleLat = (point.latitude + nextPoint.latitude) / 2;
            const middleLng = (point.longitude + nextPoint.longitude) / 2;

            return (
                <PseudoMarker
                    key={`pseudo-${index}`}
                    position={[middleLat, middleLng]}
                    index={nextIndex}
                    onPseudoMarkerClick={handlePseudoMarkerClick}
                />
            );
        });
    }, [coordinates, handlePseudoMarkerClick]);

    const getRegularMarkers = useCallback(() => {
        return coordinates.map((point, index) => (
            <FieldMarker
                key={`marker-${index}`}
                position={[point.latitude, point.longitude]}
                index={index}
                isSelected={selectedMarkerIndex === index}
                onMarkerClick={handleMarkerClick}
                onMarkerDragEnd={handleMarkerDragEnd}
            />
        ));
    }, [
        coordinates,
        selectedMarkerIndex,
        handleMarkerClick,
        handleMarkerDragEnd,
    ]);

    const getMapInstructions = () => {
        if (coordinates.length === 0) {
            return t('fieldFormMapInstructionsInitial');
        }

        if (coordinates.length < 3) {
            return t('fieldFormMapInstructionsAdditional', {
                count: 3 - coordinates.length,
            });
        }

        return t('fieldFormMapInstructionsContinue');
    };

    const increaseZoom = useCallback(() => {
        if (mapRef.current) {
            trackEvent(trackingEvents.fieldFormZoomInClick);
            const newZoom = Math.min(zoomLevel + 1, 18);
            setZoomLevel(newZoom);
            mapRef.current.setZoom(newZoom);
        }
    }, [zoomLevel, trackEvent]);

    const decreaseZoom = useCallback(() => {
        if (mapRef.current) {
            trackEvent(trackingEvents.fieldFormZoomOutClick);
            const newZoom = Math.max(zoomLevel - 1, 1);
            setZoomLevel(newZoom);
            mapRef.current.setZoom(newZoom);
        }
    }, [zoomLevel, trackEvent]);

    const handleZoomEnd = useCallback(() => {
        if (mapRef.current) {
            setZoomLevel(mapRef.current.getZoom());
        }
    }, []);

    const isLoading =
        isLoadingCreateField || isLoadingUpdateField || isLoadingFieldData;

    return (
        <Container>
            <FormContainer>
                <InputText
                    isRequired
                    label={t('fieldFormNameLabel')}
                    name="fieldName"
                    id="fieldName"
                    placeholder={t('fieldFormNamePlaceholder')}
                    value={fieldName}
                    onChange={setFieldName}
                    isLoading={isLoadingFieldData}
                />

                <InputText
                    label={t('fieldFormDescriptionLabel')}
                    name="description"
                    id="description"
                    placeholder={t('fieldFormDescriptionPlaceholder')}
                    value={description}
                    onChange={setDescription}
                    isLoading={isLoadingFieldData}
                />

                <DatePicker
                    label={t('fieldFormDatePurchaseLabel')}
                    name="datePurchase"
                    id="datePurchase"
                    placeholder={t('fieldFormDatePurchasePlaceholder')}
                    value={datePurchase}
                    onChange={(date) => setDatePurchase(date || '')}
                    isLoading={isLoadingFieldData}
                />

                <Select
                    label={t('fieldFormRentLabel')}
                    name="rent"
                    id="rent"
                    value={String(rent)}
                    onChange={(value) => setRent(value === 'true')}
                    options={[
                        { label: t('fieldFormYes'), value: 'true' },
                        { label: t('fieldFormNo'), value: 'false' },
                    ]}
                    isLoading={isLoadingFieldData}
                />

                <Select
                    label={t('fieldFormIsActiveLabel')}
                    name="isActive"
                    id="isActive"
                    value={String(isActive)}
                    onChange={(value) => setIsActive(value === 'true')}
                    options={[
                        { label: t('fieldFormYes'), value: 'true' },
                        { label: t('fieldFormNo'), value: 'false' },
                    ]}
                    isLoading={isLoadingFieldData}
                />

                <div className="area-display">
                    <span>{t('fieldFormCalculatedArea')}:</span>
                    <strong>
                        {convertFloatToBR(area, 2)}{' '}
                        {account?.unitySize === 'METER'
                            ? t('fieldFormAreaUnitMeters')
                            : t('fieldFormAreaUnitHectares')}
                    </strong>
                </div>

                <div className="form-actions">
                    <Button
                        onClick={resetField}
                        variation="secondary"
                        style={{ marginRight: '10px' }}
                        isDisabled={isLoading}
                    >
                        {t('fieldFormClearPoints')}
                    </Button>
                    <Button
                        onClick={saveField}
                        isDisabled={
                            !fieldName || coordinates.length < 3 || isLoading
                        }
                        variation="primary"
                        isLoading={isLoading}
                    >
                        {t(id ? 'fieldFormUpdateArea' : 'fieldFormSaveArea')}
                    </Button>
                </div>
            </FormContainer>

            <div className="map-container">
                <AreaInfo area={area} loading={isLoadingLocation} />

                <Map
                    center={defaultCenter}
                    zoom={zoomLevel}
                    whenCreated={(map) => {
                        mapRef.current = map;
                        map.on('zoomend', handleZoomEnd);
                    }}
                    scrollWheelZoom={!isDraggingMarker}
                    dragging={!isDraggingMarker}
                    zoomControl={false}
                >
                    <MapController
                        onMapClick={handleMapClick}
                        shouldFitBounds={coordinates.length > 1}
                        bounds={bounds}
                        useCurrentLocation={isUsingCurrentLocation}
                    />

                    <FieldPolygon coordinates={coordinates} />

                    {getPseudoMarkers()}
                    {getRegularMarkers()}
                </Map>

                <div className="map-instructions">{getMapInstructions()}</div>

                <ZoomButtonsContainer>
                    <ActionButtons onClick={increaseZoom}>
                        <AiOutlinePlus />
                    </ActionButtons>
                    <ActionButtons onClick={decreaseZoom}>
                        <AiOutlineMinus />
                    </ActionButtons>
                </ZoomButtonsContainer>

                <ActionButtonsContainer>
                    <ActionButtons onClick={handleMyLocation}>
                        <MdMyLocation />
                    </ActionButtons>

                    <HistoryController
                        history={history}
                        setCoordinates={setCoordinates}
                        setHistory={setHistory}
                    />

                    {selectedMarkerIndex !== null && (
                        <ActionButtons onClick={handleDeleteMarker}>
                            <MdDelete />
                        </ActionButtons>
                    )}
                </ActionButtonsContainer>
            </div>
        </Container>
    );
};
